import { Trans } from "@lingui/react/macro";
import { useNavigate } from "react-router-dom";
import { Box, Button, Divider, TextField } from "@mui/material";
import { redirectForMicrosoftLogin } from "../../helpers/redirectToMicrosoftLogin";
import LoginContainer from "./loginContainer";
import MicrosoftImg from "../../images/microsoft_icon.svg";
import BackgroundImg from "../../images/background.png";
import tokenStorageHelper from "../../helpers/tokenStorage";
import { DecodedToken } from "../../api/types";
import { Navigate } from "react-router";
import { jwtDecode } from "jwt-decode";
import { t } from "@lingui/macro";
import ErrorAlert from "../errorAlert";
import { useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import { authenticateWithPassword } from "../../api/auth";
import { isValidEmail } from "../../helpers/isValidEmail";

const invalidEmail = new Error("Invalid Email");
const missingPassword = new Error("Missing Password");
const suppressAlerts: Array<Error | null> = [invalidEmail, missingPassword];

const Login = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<Error | null>(null);

const handleSubmit = async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    const data = new FormData(event.currentTarget as HTMLFormElement);
    const email = data.get("email");
    const password = data.get("password");

    if (!isValidEmail(email?.toString())) {
      setLoginError(invalidEmail);
      return;
    } else if (password?.toString().length === 0) {
      setLoginError(missingPassword);
      return;
    }

    setLoginError(null);
    setIsLoading(true);
    try {
      await authenticateWithPassword(
        email?.toString() || "",
        password?.toString() || ""
      );
    } catch (ex) {
      setLoginError(ex as any);
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenStorageHelper.isValid) {
    var decodedToken = jwtDecode(tokenStorageHelper.token) as
      | DecodedToken
      | undefined;
    if (decodedToken) {
      if (decodedToken.hasConversionManagement === "False") {
        if (decodedToken.hasTaxonomyManagement === "True") {
          return <Navigate to="/taxonomies" replace={true} />;
        }
        if (decodedToken.hasTaxonomyManagement === "True") {
          return <Navigate to="/taxonomies" replace={true} />;
        }
        if (decodedToken.hasUserManagement === "True") {
          return <Navigate to="/users" replace={true} />;
        }
        if (decodedToken.hasValidationManagement === "True") {
          return <Navigate to="/validation" replace={true} />;
        }
        if (decodedToken.hasCustomObjectManagement === "True") {
          return <Navigate to="/customobjects" replace={true} />;
        }
      } else {
        return <Navigate to="/conversions" replace={true} />;
      }
    }
  }
  return (
    <Box
      height="100vh"
      sx={{
        backgroundImage: `url(${BackgroundImg})`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }}
    >
      <LoginContainer sx={{ pt: 15 }} additionalButton={
          <Button
            fullWidth
            onClick={() => navigate("/accounts/password/forgot")}>
            <Trans>Forgot Password?</Trans>
          </Button>
        }>
        <Box sx={{ mt: 1 }}>
          <ErrorAlert
            formError={suppressAlerts.includes(loginError) ? null : loginError}
          />
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              fullWidth
              id="email"
              label={<Trans>Email Address</Trans>}
              name="email"
              autoComplete="email"
              helperText={
                loginError === invalidEmail ? (
                  <Trans>Please enter a valid email address.</Trans>
                ) : null
              }
              error={loginError === invalidEmail}
              autoFocus
            />
            <TextField
              margin="normal"
              fullWidth
              name="password"
              label={<Trans>Password</Trans>}
              type="password"
              id="password"
              helperText={
                loginError === missingPassword
                  ? "Please enter your password."
                  : null
              }
              error={loginError === missingPassword}
              autoComplete="current-password"
            />
            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              loading={isLoading}
              sx={{ mt: 3, mb: 2, height: 45 }}>
              <Trans>Log In</Trans>
            </LoadingButton>
          </Box>
          <Divider>
            <Trans>OR</Trans>
          </Divider>
          <Button
            color="inherit"
            variant="outlined"
            startIcon={<img src={MicrosoftImg} width="20px" alt="Microsoft" />}
            fullWidth
            onClick={redirectForMicrosoftLogin}
            sx={{ mt: 3, mb: 2 }}
          >
            {t`Sign in with Microsoft`}
          </Button>
        </Box>
      </LoginContainer>
    </Box>
  );
};

export default Login;
